/**
 * Where the course is on a given day (slice B, P1-1/P1-2/P1-3). Pure: the caller passes the
 * local day key (`dayKey(now, "Europe/Stockholm")`), so the client card, the topics page and the
 * server's Dagens pass all read the same state from the same `content/courses/*.json` dates.
 *
 * The catalogue lists F01–F12 before R01–R03, so lectures are put in date order first (ties keep
 * the catalogue order). A lecture whose date is before `today` is `done`, one on `today` is
 * `today`, a later one `upcoming`. The JSON has no start times, so a lecture is never "done" on
 * its own day: the card says "Idag: F03" all day and "Repetera F03" from the next morning.
 */

/** The one course this deployment serves (review M9: one constant for client and server). */
export const CURRENT_COURSE_ID = "HT2026";

export interface DatedLecture {
  id: string;
  /** `YYYY-MM-DD`, a calendar day in the app timezone. */
  date: string;
}

export type LectureState = "done" | "today" | "upcoming";

export interface LectureTimeline<T extends DatedLecture> {
  /** Every lecture in date order. */
  ordered: T[];
  /** Lectures before `today`, in date order. */
  done: T[];
  /** The lecture held on `today`, if any. */
  today: T | null;
  /** The first lecture after `today`, if any. */
  next: T | null;
  /** The lecture to prepare: `today` if there is one, else `next`; null after the last lecture. */
  focus: T | null;
  /** The latest lecture before `today` (the one to review), if any. */
  previous: T | null;
}

export function lectureState(date: string, today: string): LectureState {
  return date < today ? "done" : date === today ? "today" : "upcoming";
}

export function lectureTimeline<T extends DatedLecture>(
  lectures: readonly T[],
  today: string,
): LectureTimeline<T> {
  const ordered = lectures
    .map((lecture, index) => ({ lecture, index }))
    .sort((a, b) =>
      a.lecture.date < b.lecture.date
        ? -1
        : a.lecture.date > b.lecture.date
          ? 1
          : a.index - b.index,
    )
    .map(({ lecture }) => lecture);
  const done = ordered.filter((l) => l.date < today);
  const todays = ordered.find((l) => l.date === today) ?? null;
  const next = ordered.find((l) => l.date > today) ?? null;
  return {
    ordered,
    done,
    today: todays,
    next,
    focus: todays ?? next,
    previous: done.length > 0 ? done[done.length - 1] : null,
  };
}

/**
 * "Bara det vi gått igenom (t.o.m. F03)" (slice D, P1-6): the latest lecture held on or before
 * the day, today's included (a student who practises the evening after F03 has had F03).
 */
export function lastTaught<T extends DatedLecture>(timeline: LectureTimeline<T>): T | null {
  return timeline.today ?? timeline.previous;
}

/**
 * The topics of every lecture up to and including `throughId`, in date order (ties keep the
 * catalogue order); null when no lecture has that id. A topic no such lecture lists is not taught.
 */
export function taughtTopics<T extends DatedLecture & { topics: readonly string[] }>(
  lectures: readonly T[],
  throughId: string,
): Set<string> | null {
  const ordered = lectureTimeline(lectures, "0000-00-00").ordered;
  const end = ordered.findIndex((lecture) => lecture.id === throughId);
  if (end < 0) return null;
  return new Set(ordered.slice(0, end + 1).flatMap((lecture) => lecture.topics));
}
